"use client";

import type { FormStep } from "./form-step";

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { parseStepIndexFromHistory } from "~www/hooks/parse-step-from-history";

type UseLeadFormBrowserHistoryParams = {
  steps: FormStep[];
  formId: string;
  currentStepIndex: number;
  /** True once submitted (Thank You showing) — see the popstate handler below. */
  isSubmitted?: boolean;
  syncToStepIndex: (targetStepIndex: number) => void;
};

function isSkipOnBackStep(step: FormStep) {
  return step._type === "interstitialStep" && step.autoAdvance === true;
}

/**
 * A backward popstate can report the index of a `skipOnBack` auto-advancing
 * interstitial, which isn't a valid BACK landing target. Walk past it,
 * mirroring the XState machine's own BACK transition.
 */
function resolveBackwardTargetIndex(steps: FormStep[], rawTargetIndex: number) {
  let index = rawTargetIndex;
  while (index > 0 && isSkipOnBackStep(steps[index])) {
    index--;
  }
  return index;
}

/**
 * Wires LFB step navigation to the browser history API, mirroring the
 * legacy `useMultiStepForm` hook: forward navigation pushes a history entry,
 * Back defers to `history.back()`, and a shared `popstate` listener realigns
 * the machine via `syncToStepIndex` for both browser Back/Forward and our
 * own calls. Once submitted, popstate just keeps consuming this form's own
 * entries via `history.back()` so one real Back press exits the form.
 */
function useLeadFormBrowserHistory({
  currentStepIndex,
  formId,
  isSubmitted,
  steps,
  syncToStepIndex,
}: UseLeadFormBrowserHistoryParams) {
  const finalStepIndex = steps.length - 1;

  // Read from a ref so pushNextStepToHistory/goBackHistory/goToFirstStepHistory
  // don't need to be recreated on every step change, which would bust
  // memoization on step components. Synced via useLayoutEffect, not a
  // passive useEffect: a passive effect only mirrors the value once the
  // browser is idle after paint, leaving a window where a click right after
  // an advance (e.g. closing the takeover it just opened) could read a
  // stale index and no-op.
  const currentStepIndexRef = useRef(currentStepIndex);
  useLayoutEffect(() => {
    currentStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex]);

  // Same reasoning as currentStepIndexRef.
  const isSubmittedRef = useRef(isSubmitted ?? false);
  useLayoutEffect(() => {
    isSubmittedRef.current = isSubmitted ?? false;
  }, [isSubmitted]);

  // Guards goBackHistory/goToFirstStepHistory against a rapid second click:
  // history.back()/go() resolve asynchronously via popstate, so a second
  // click before that lands would issue another call and pop too far.
  const isGoingBackRef = useRef(false);

  useEffect(() => {
    const syncFromHistory = () => {
      isGoingBackRef.current = false;

      const rawHistoryState = history.state as {
        formId?: string;
        stepIndex?: number;
      } | null;
      const isOwnHistoryEntry = rawHistoryState?.formId === formId;

      // Done — keep consuming this form's entries via history.back() instead
      // of resyncing the machine, so one real Back press exits the form.
      if (isSubmittedRef.current) {
        if (isOwnHistoryEntry) {
          history.back();
        }
        return;
      }

      const rawTargetIndex = parseStepIndexFromHistory({
        finalStepIndex,
        formId,
        initialStepIndex: 0,
      });

      const isMovingBackward = rawTargetIndex < currentStepIndexRef.current;

      if (!isMovingBackward) {
        syncToStepIndex(rawTargetIndex);
        return;
      }

      const targetIndex = resolveBackwardTargetIndex(steps, rawTargetIndex);
      syncToStepIndex(targetIndex);

      // Each skipped skipOnBack step owns its own real history entry; walk
      // past the rest in the same tick so the next Back press lands on real
      // content instead of another skipOnBack ghost entry.
      const skippedEntryCount = rawTargetIndex - targetIndex;
      if (skippedEntryCount > 0) {
        history.go(-skippedEntryCount);
      }
    };

    window.addEventListener("popstate", syncFromHistory);

    return () => {
      window.removeEventListener("popstate", syncFromHistory);
    };
  }, [finalStepIndex, formId, steps, syncToStepIndex]);

  const pushNextStepToHistory = useCallback(() => {
    const nextStepIndex = currentStepIndexRef.current + 1;

    // NEXT is a no-op on the last step, so don't push an out-of-range entry.
    if (nextStepIndex > finalStepIndex) {
      return;
    }

    // The browser history API, not router.push, since we only need to
    // update history state.
    history.pushState(
      { ...history.state, formId, stepIndex: nextStepIndex },
      "",
    );
  }, [finalStepIndex, formId]);

  const goBackHistory = useCallback(() => {
    if (currentStepIndexRef.current <= 0 || isGoingBackRef.current) {
      return;
    }
    isGoingBackRef.current = true;
    history.back();
  }, []);

  // Unwinds every entry this form pushed in one shot — the takeover close
  // button's mechanism.
  const goToFirstStepHistory = useCallback(() => {
    const entriesToUnwind = currentStepIndexRef.current;
    if (entriesToUnwind <= 0 || isGoingBackRef.current) {
      return;
    }
    isGoingBackRef.current = true;
    history.go(-entriesToUnwind);
  }, []);

  return { goBackHistory, goToFirstStepHistory, pushNextStepToHistory };
}

export { useLeadFormBrowserHistory };
